import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import {
  ArrowRight,
  EnvelopeSimple,
  LockKey,
  Eye,
  EyeSlash,
  ShieldCheck,
  CircleNotch,
} from "@phosphor-icons/react";
import { loginSchema } from "@reactive-resume/dto";
import { usePasswordToggle } from "@reactive-resume/hooks";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import type { z } from "zod";
import { motion } from "framer-motion";

import { useLogin } from "@/client/services/auth";
import { useFeatureFlags } from "@/client/services/feature";
import { SocialAuth } from "../_components/social-auth";

type FormValues = z.infer<typeof loginSchema>;

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: {
    delay: i * 0.055,
    duration: 0.4,
    ease,
  },
});

export const LoginPage = () => {
  const { login, loading } = useLogin();
  const { flags } = useFeatureFlags();
  const [showPwd, setShowPwd] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  usePasswordToggle(formRef);

  const form = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await login(data);
    } catch {
      form.setValue("password", "");
    }
  };

  return (
    <>
      <Helmet>
        <title>{t`Se connecter`} — Mon CV Pro</title>
      </Helmet>

      {/* Eyebrow */}
      <motion.p
        {...fadeUp(0)}
        className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.2em] text-[#3ecf8e]"
      >
        BON RETOUR PARMI NOUS
      </motion.p>

      {/* Titre */}
      <motion.h1
        {...fadeUp(1)}
        className="mb-1 text-[1.85rem] font-extrabold leading-[1.18] tracking-[-0.025em] text-gray-900"
      >
        Connectez-vous à votre espace
      </motion.h1>

      {/* Sous-titre */}
      <motion.p {...fadeUp(2)} className="mb-5 text-[0.84rem] leading-relaxed text-gray-400">
        Retrouvez vos CV, vos candidatures et l'évolution de votre profil.
      </motion.p>

      {flags.isEmailAuthDisabled && (
        <Alert variant="error" className="mb-4 rounded-xl">
          <AlertTitle>
            {t`Signing in via email is currently disabled by the administrator.`}
          </AlertTitle>
        </Alert>
      )}

      {/* Google / Social — visible aussi sur la page login */}
      <motion.div {...fadeUp(3)}>
        <SocialAuth />
      </motion.div>

      <div className={cn(flags.isEmailAuthDisabled && "pointer-events-none select-none blur-sm")}>
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-y-3.5"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* E-mail */}
            <motion.div {...fadeUp(4)}>
              <FormField
                name="identifier"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                      {t`Adresse e-mail`}
                    </FormLabel>

                    <FormControl>
                      <div className="relative">
                        <EnvelopeSimple
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                        />

                        <Input
                          autoComplete="email"
                          placeholder="exemple@domaine.com"
                          className={cn(
                            "h-11 rounded-xl border border-[#C9D3E6]",
                            "bg-white pl-10 text-sm font-medium text-[#172033]",
                            "caret-[#5B72E8] shadow-sm",
                            "placeholder:font-normal placeholder:text-[#98A2B3]",
                            "transition-[border-color,box-shadow,background-color]",
                            "hover:border-[#AEBDE0]",
                            "focus-visible:border-[#6687F0]",
                            "focus-visible:bg-white",
                            "focus-visible:ring-2 focus-visible:ring-[#6687F0]/20",
                            " ",
                            "",
                            "",
                            "",
                          )}
                          {...field}
                        />
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </motion.div>

            {/* Mot de passe */}
            <motion.div {...fadeUp(5)}>
              <FormField
                name="password"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-[0.78rem] font-semibold text-gray-600">
                        {t`Mot de passe`}
                      </FormLabel>

                      <Link
                        to="/client/auth/forgot-password"
                        className="text-[0.74rem] font-medium text-[#472cb3] transition-colors hover:underline"
                      >
                        {t`Mot de passe oublié ?`}
                      </Link>
                    </div>

                    <FormControl>
                      <div className="relative">
                        <LockKey
                          size={16}
                          className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085]"
                        />

                        <Input
                          type={showPwd ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder={t`Saisissez votre mot de passe`}
                          className={cn(
                            "h-11 rounded-xl border border-[#C9D3E6]",
                            "bg-white pl-10 pr-11 text-sm font-medium text-[#172033]",
                            "caret-[#5B72E8] shadow-sm",
                            "placeholder:font-normal placeholder:text-[#98A2B3]",
                            "transition-[border-color,box-shadow,background-color]",
                            "hover:border-[#AEBDE0]",
                            "focus-visible:border-[#6687F0]",
                            "focus-visible:bg-white",
                            "focus-visible:ring-2 focus-visible:ring-[#6687F0]/20",
                            " ",
                            "",
                            "",
                            "",
                          )}
                          {...field}
                        />

                        <button
                          type="button"
                          onClick={() => setShowPwd((v) => !v)}
                          className="absolute right-3.5 top-1/2 z-10 -translate-y-1/2 text-[#667085] transition-colors hover:text-[#465BBD]"
                          tabIndex={-1}
                          aria-label={showPwd ? "Masquer" : "Afficher"}
                        >
                          {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </FormControl>

                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </motion.div>

            {/* Bouton Se connecter */}
            <motion.div {...fadeUp(6)} className="pt-0.5">
              <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
                <Button
                  type="submit"
                  disabled={loading}
                  className="group flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#472cb3] text-[0.92rem] font-semibold text-white shadow-[0_4px_16px_rgba(71,44,179,0.3)] transition-all duration-200 hover:bg-[#3a22a0] active:bg-[#311b8a]"
                >
                  {loading ? (
                    <CircleNotch size={16} weight="bold" className="animate-spin" />
                  ) : (
                    <>
                      {t`Se connecter`}

                      <ArrowRight
                        size={16}
                        weight="bold"
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.div>

            {/* Séparateur */}
            <motion.div {...fadeUp(7)} className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-100" />

              <span className="text-[0.74rem] text-gray-400">{t`Nouveau sur Mon CV Pro ?`}</span>

              <div className="h-px flex-1 bg-gray-100" />
            </motion.div>

            {/* Créer mon compte */}
            <motion.div {...fadeUp(8)}>
              <motion.div whileHover={{ scale: 1.012 }} whileTap={{ scale: 0.988 }}>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 w-full rounded-2xl border-2 border-[#472cb3] bg-white text-[0.92rem] font-semibold text-[#472cb3] transition-all duration-200 hover:bg-[#f5f2ff]"
                >
                  <Link to="/client/auth/register">{t`Créer mon compte`}</Link>
                </Button>
              </motion.div>
            </motion.div>

            {/* Sécurité */}
            <motion.div {...fadeUp(9)} className="flex items-center justify-center gap-1.5">
              <ShieldCheck size={13} className="shrink-0 text-[#3ecf8e]" />

              <span className="text-[0.72rem] text-gray-400">
                {t`Connexion sécurisée et données protégées.`}
              </span>
            </motion.div>
          </form>
        </Form>
      </div>
    </>
  );
};
